import type { AxiosInstance } from 'axios'
import type { Alpha3Code } from 'i18n-iso-countries'

import { type OpportunityLocationDto, OpportunityType, OpportunityLocationTypeDto } from '@percent/domain/volunteering'
import type { PBPResponse } from '../types/pbpResponses.types'

export { OpportunityType, OpportunityLocationDto, OpportunityLocationTypeDto }

export type VolunteeringPartnerServiceCapabilities = {
  percentClient: AxiosInstance
}

export type VolunteeringPartner = {
  id: string
  name: string
  logo?: string | null
}

export enum OpportunityStatus {
  ACTIVE = 'published',
  CLOSED = 'closed',
  CANCELLED = 'cancelled'
}

export type OpportunityTimeTracking = 'automatic' | 'manual'

export type CausesPortalOpportunityDto = Readonly<{
  id: string
  name: string
  type: OpportunityType
  location: OpportunityLocationDto
  status: OpportunityStatus
  startDate: string | null
  endDate: string | null
  timeTracking: OpportunityTimeTracking
  participants: { spots: number | null; count: number }
  organiser: { id: string; email: string | null; fullName: string | null } | null
  createdAt: string
  featured: { featuredItemId: string; country: string[] } | null
  partner: { id: string; name: string; logo: string | null }
}>

export type GetVolunteeringOrganisationCreatedOpportunitiesQuery = {
  organisationId: string
  pageSize?: number | null
  cursor?: string
}

export type VolunteeringSkillDto = Readonly<{
  id: string
  name: string
}>

export type VolunteeringActivityDto = VolunteeringSkillDto

export type OpportunityDetailDto = Readonly<{
  id: string
  name: string
  description: string | null
  parentId: string | null
  image: string | null
  type: OpportunityType
  status: OpportunityStatus
  location: OpportunityLocationDto
  startDate: string | null
  endDate: string | null
  partnerId: string
  organisation: {
    id: string
    name: string
    logo: string | null
    countryCode: string
  } | null
  organiser: {
    id: string
    fullName: string
    image: string | null
    email: string
  } | null
  skills: VolunteeringSkillDto[]
  spots: number | null
  participantsCount: number
  activities: VolunteeringActivityDto[]
  itinerary:
    | {
        id: number
        durationMinutes: number
        title: string
        description: string | null
      }[]
    | null
  participation?: {
    status: 'participating' | 'not_participating' | 'awaiting_approval'
  } | null
  timeTracking: OpportunityTimeTracking
  external: {
    provider: string
  } | null
  managedByOrganisation?: boolean
  cancelledBy: string | null
  notificationsEnabled: boolean
  targetCountries: Alpha3Code[]
}>

export interface CreateVolunteeringEventPayload {
  type: OpportunityType.Event
  partnerId: string
  name: string
  description: string
  location: OpportunityLocationDto
  startDate: Date
  endDate: Date
  participantSpots?: number
  timeTracking?: OpportunityTimeTracking
  skills: string[]
  activities: string[]
  notificationsEnabled: boolean
  image?: File | string
  targetCountries?: Alpha3Code[]
}

export interface EditVolunteeringOpportunityPayload {
  name?: string
  description?: string
  location?: OpportunityLocationDto
  startDate?: Date
  endDate?: Date
  participantSpots?: number
  timeTracking?: OpportunityTimeTracking
  skills?: string[]
  activities?: string[]
  notificationsEnabled?: boolean
  image?: File | string
  targetCountries?: Alpha3Code[]
}

export type VolunteeringRegion = Readonly<{
  id: string
  name: string
  countryCodes: Alpha3Code[]
}>

export type GetVolunteeringPartnerRegionsResponse = PBPResponse<Record<string, VolunteeringRegion>>
